import React, { useState, useRef, useEffect } from 'react';

interface AnimationProgressSliderProps {
  currentFrame: number;
  onFrameChange: (frame: number) => void;
  isPlaying: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

const AnimationProgressSlider: React.FC<AnimationProgressSliderProps> = ({
  currentFrame,
  onFrameChange,
  isPlaying,
  onDragStart,
  onDragEnd,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localFrame, setLocalFrame] = useState(currentFrame);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Update local frame when prop changes (but not during drag)
  useEffect(() => {
    if (!isDragging) {
      setLocalFrame(currentFrame);
    }
  }, [currentFrame, isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    onDragStart?.();
    updateFrameFromEvent(e.clientX);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    onDragStart?.();
    updateFrameFromEvent(e.touches[0].clientX);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      updateFrameFromEvent(e.clientX);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging) {
      e.preventDefault();
      updateFrameFromEvent(e.touches[0].clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const updateFrameFromEvent = (clientX: number) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    const newFrame = Math.max(0, Math.min(percentage, 100));
    
    setLocalFrame(newFrame);
    onFrameChange(newFrame);
  };

  // Add/remove global mouse and touch event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const displayFrame = isDragging ? localFrame : currentFrame;

  return (
    <div
      ref={sliderRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        width: '100%',
        height: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        position: 'relative',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        pointerEvents: 'auto',
        touchAction: 'none',
      }}
    >
        {/* Progress fill */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${displayFrame}%`,
            backgroundColor: '#6366f1',
            borderRadius: '6px',
            transition: isDragging ? 'none' : 'width 0.05s linear',
          }}
        />
        
        {/* Draggable handle */}
        <div
          style={{
            position: 'absolute',
            left: `${displayFrame}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            backgroundColor: 'white',
            borderRadius: '50%',
            border: '2px solid #6366f1',
            cursor: isDragging ? 'grabbing' : 'grab',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'auto',
          }}
        />
    </div>
  );
};

export default AnimationProgressSlider;
